import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 093 Worker Application Inbox API.
// Every query param is optional - visibility scoping (Agent/PLC/SuperAgent/
// SuperAdmin) always happens on the backend from the caller's own session,
// never from a value this file sends.

const LIST_PATH = "/backoffice/worker-applications";

// Exactly the param names the P093 contract accepts - anything else passed
// to listWorkerApplications is silently dropped rather than forwarded, so a
// typo'd key can never accidentally reach the backend as an unfiltered
// pass-through.
const LIST_PARAM_KEYS = [
  "page",
  "pageSize",
  "status",
  "nationality",
  "preferredLanguageCode",
  "agentId",
  "superAgentOrganizationId",
  "plcOrganizationId",
  "inviteCardId",
  "dateFromUtc",
  "dateToUtc",
  "search",
];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`). */
function buildListQuery(params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of LIST_PARAM_KEYS) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listWorkerApplications(params = {}) {
  return apiClient.get(`${LIST_PATH}${buildListQuery(params)}`);
}

export function getWorkerApplication(applicationId) {
  return apiClient.get(`${LIST_PATH}/${encodeURIComponent(applicationId)}`);
}

// P141R16: the real passport/identity document image, fetched as binary
// (never JSON) - returns { blob, contentType }. A 404 ApiClientError means
// no document was ever uploaded for this application (or, for a promoted
// worker, its source application) - callers must treat that as an honest
// empty state, never a fabricated placeholder image.
export function getWorkerApplicationPassportDocumentBlob(applicationId) {
  return apiClient.getBlob(`${LIST_PATH}/${encodeURIComponent(applicationId)}/passport-document/file`);
}

// Prompt 138W-R5: the real Prompt 095 "start e-KYC" endpoint - confirmed
// against EkycReviewService.StartEkycAsync to be genuinely callable by the
// owning Agent (ActorOwnsAgent(actor, application.AgentId)), not just
// SuperAdmin. Returns an EkycReviewDetailDto (a different shape from the
// worker-application detail this file's other functions return) - callers
// only need this call's success/failure, not its response body, so nothing
// here reshapes it.
export function startEkyc(applicationId, { riskLevel } = {}) {
  return apiClient.post(`${LIST_PATH}/${encodeURIComponent(applicationId)}/ekyc/start`, riskLevel ? { riskLevel } : {});
}
